import { useState, useEffect, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { useDropzone } from "react-dropzone";
import { 
  Search, Share2, FileText, Target, Code, Palette, ArrowRight, Mail, MapPin, Send, CheckCircle, Menu, X,
  Lightbulb, Rocket, Heart, Sparkles, ArrowLeft, Briefcase, Upload, Users, LayoutDashboard, Settings,
  FileUser, Newspaper, MessageSquare, LogOut, Eye, Trash2, Edit, Plus, Building, Clock, ChevronDown,
  BarChart3, Globe, Monitor, Smartphone, Tablet, TrendingUp, Activity, MousePointer, UserPlus, XCircle,
  GraduationCap
} from "lucide-react";
import { Toaster, toast } from "sonner";

// Pre-Joining Portal Components
import PreJoiningLogin from "./components/PreJoiningLogin";
import PreJoiningDashboard from "./components/PreJoiningDashboard";
import PreJoiningAdmin from "./components/PreJoiningAdmin";

// Joined Portal (Training) Components
import JoinedPortalLogin from "./components/JoinedPortalLogin";
import JoinedPortalDashboard from "./components/JoinedPortalDashboard";
import JoinedPortalAdmin from "./components/JoinedPortalAdmin";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============ VISITOR TRACKING ============
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('visitor_session');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    sessionStorage.setItem('visitor_session', sessionId);
  }
  return sessionId;
};

const trackVisit = async (page) => {
  try {
    await axios.post(`${API}/track/visit`, {
      page: page,
      referrer: document.referrer || null,
      user_agent: navigator.userAgent,
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      session_id: getSessionId()
    });
  } catch (e) {
    // Silent fail for tracking
  }
};

// Visitor Tracking Hook
const useVisitorTracking = () => {
  const location = useLocation();
  
  useEffect(() => {
    trackVisit(location.pathname);
  }, [location.pathname]);
};

// ============ AUTH CONTEXT ============
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('adminToken'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.get(`${API}/admin/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(res => {
        setAdmin(res.data);
        setLoading(false);
      })
      .catch(() => {
        localStorage.removeItem('adminToken');
        setToken(null);
        setAdmin(null);
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/admin/login`, { email, password });
    localStorage.setItem('adminToken', res.data.access_token);
    setToken(res.data.access_token);
    setAdmin(res.data.admin);
    return res.data;
  };

  const register = async (name, email, password) => {
    const res = await axios.post(`${API}/admin/register`, { name, email, password });
    localStorage.setItem('adminToken', res.data.access_token);
    setToken(res.data.access_token);
    setAdmin(res.data.admin);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    setToken(null);
    setAdmin(null);
  };

  return (
    <AuthContext.Provider value={{ admin, token, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.1 } }
};

// ============ SHARED COMPONENTS ============

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const navItems = [
    { path: '/about', label: 'About' },
    { path: '/services', label: 'Services' },
    { path: '/careers', label: 'Careers' },
    { path: '/contact', label: 'Contact' }
  ];

  return (
    <nav className="nav-elegant fixed top-0 left-0 right-0 z-50" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigate('/')}
          >
            <div className="w-10 h-10 rounded bg-navy flex items-center justify-center">
              <span className="font-playfair font-bold text-white text-lg">CI</span>
            </div>
            <div className="flex flex-col">
              <span className="font-playfair font-semibold text-xl text-charcoal">Creative Infotech</span>
              <span className="text-xs text-charcoal-muted tracking-wider">Digital Marketing Excellence</span>
            </div>
          </motion.div>

          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item, i) => (
              <motion.div key={item.path} initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
                <Link to={item.path} className="font-dm text-charcoal-muted hover:text-navy transition-colors text-sm font-medium" data-testid={`nav-${item.label.toLowerCase()}`}>
                  {item.label}
                </Link>
              </motion.div>
            ))}
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3 }}>
              <Link to="/contact" className="btn-primary text-sm" data-testid="nav-cta">Get a Quote</Link>
            </motion.div>
          </div>

          <button className="md:hidden text-charcoal" onClick={() => setIsOpen(!isOpen)} data-testid="mobile-menu-toggle">
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {isOpen && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="md:hidden bg-white border-t border-gray-100">
          <div className="px-6 py-6 space-y-4">
            {navItems.map((item) => (
              <Link key={item.path} to={item.path} onClick={() => setIsOpen(false)} className="block w-full text-left font-dm text-charcoal-muted hover:text-navy py-2">
                {item.label}
              </Link>
            ))}
            <Link to="/contact" onClick={() => setIsOpen(false)} className="btn-primary w-full text-sm block text-center">Get a Quote</Link>
          </div>
        </motion.div>
      )}
    </nav>
  );
};

const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  return (
    <footer className="footer-elegant py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded bg-gold flex items-center justify-center">
                <span className="font-playfair font-bold text-white text-lg">CI</span>
              </div>
              <div>
                <span className="font-playfair font-semibold text-xl text-white block">Creative Infotech</span>
                <span className="text-xs text-white/60">Digital Marketing Excellence</span>
              </div>
            </div>
            <p className="font-dm text-white/70 text-sm leading-relaxed max-w-md mb-6">
              A dynamic digital marketing agency based in Gujarat, India, helping businesses grow online with innovative strategies since 2024.
            </p>
          </div>

          <div>
            <h4 className="font-playfair text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-3">
              {['About', 'Services', 'Careers', 'Contact'].map((item) => (
                <li key={item}><Link to={`/${item.toLowerCase()}`} className="font-dm text-sm text-white/70 hover:text-gold transition-colors">{item}</Link></li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-playfair text-white font-semibold mb-4">Contact</h4>
            <ul className="space-y-3">
              <li className="font-dm text-sm text-white/70"><span className="text-gold">Email:</span> info@creativeinfotech.net</li>
              <li className="font-dm text-sm text-white/70"><span className="text-gold">Address:</span> Gujarat, India</li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="font-dm text-sm text-white/50">© {currentYear} Creative Infotech. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/privacy-policy" className="font-dm text-sm text-white/50 hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms-of-service" className="font-dm text-sm text-white/50 hover:text-white transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ============ HOME PAGE COMPONENTS ============

const HeroSection = () => {
  const navigate = useNavigate();
  return (
    <section className="hero-elegant min-h-screen flex items-center pt-20" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div variants={staggerContainer} initial="hidden" animate="visible">
            <motion.div variants={fadeUp} className="mb-6">
              <span className="inline-block px-4 py-2 bg-gold/10 text-gold-dark rounded-full text-sm font-dm font-medium">India's Trusted Digital Partner</span>
            </motion.div>
            <motion.h1 variants={fadeUp} className="font-playfair text-4xl sm:text-5xl lg:text-6xl text-charcoal leading-tight mb-6">
              Transforming Brands with<span className="text-navy block mt-2">Strategic Digital Marketing</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="font-dm text-lg text-charcoal-muted max-w-xl mb-8 leading-relaxed">
              We help businesses grow with result-driven digital marketing strategies. From SEO to social media, we deliver measurable outcomes that matter.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
              <button onClick={() => navigate('/contact')} className="btn-gold flex items-center gap-2 group" data-testid="hero-cta-primary">
                Start Your Project<ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button onClick={() => navigate('/services')} className="btn-outline flex items-center gap-2" data-testid="hero-cta-secondary">Our Services</button>
            </motion.div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }} className="relative hidden lg:block">
            <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=700&fit=crop&crop=faces" alt="Digital Marketing Team" className="rounded-lg shadow-2xl w-full object-cover" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const services = [
  { icon: Search, title: "SEO & Search Marketing", description: "Improve your search rankings and drive organic traffic with our proven SEO strategies." },
  { icon: Share2, title: "Social Media Marketing", description: "Build your brand presence across all major social media platforms." },
  { icon: FileText, title: "Content Marketing", description: "Create compelling content that establishes your brand as an industry authority." },
  { icon: Target, title: "PPC & Paid Advertising", description: "Maximize your ROI with data-driven paid advertising campaigns." },
  { icon: Code, title: "Web Development", description: "Get a professional, mobile-responsive website designed to convert." },
  { icon: Palette, title: "Branding & Design", description: "Create a memorable brand identity with comprehensive design services." }
];

const AboutSectionHome = () => {
  const values = [
    { icon: Lightbulb, title: 'Innovative Solutions', description: 'Fresh ideas tailored for your business growth.' },
    { icon: Rocket, title: 'Results-Driven', description: 'Measurable outcomes that impact your bottom line.' },
    { icon: Heart, title: 'Client-Centric', description: 'Your success is our priority.' },
    { icon: Sparkles, title: 'Creative Excellence', description: 'Unique campaigns that make your brand stand out.' }
  ];

  return (
    <section id="about" className="py-20 lg:py-28 bg-white" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
            <motion.span variants={fadeUp} className="text-gold font-dm font-semibold text-sm tracking-wider uppercase">About Us</motion.span>
            <motion.h2 variants={fadeUp} className="font-playfair text-3xl md:text-4xl text-charcoal mt-4 mb-6">Your Growth is Our Mission</motion.h2>
            <motion.div variants={fadeUp} className="section-divider mb-6" />
            <motion.p variants={fadeUp} className="font-dm text-charcoal-muted leading-relaxed mb-6">
              Creative Infotech is a dynamic digital marketing agency based in Gujarat, India, established in 2024. We help businesses establish a powerful online presence through innovative strategies.
            </motion.p>
            <motion.div variants={fadeUp}><Link to="/about" className="btn-outline flex items-center gap-2 inline-flex">Learn More<ArrowRight className="w-4 h-4" /></Link></motion.div>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {values.map((value, index) => (
              <motion.div key={index} variants={fadeUp} className="card-elegant text-left">
                <div className="w-12 h-12 rounded-lg bg-gold/10 flex items-center justify-center mb-4"><value.icon className="w-6 h-6 text-gold" /></div>
                <h3 className="font-playfair text-lg text-charcoal mb-2">{value.title}</h3>
                <p className="font-dm text-sm text-charcoal-muted">{value.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const ServicesSectionHome = () => {
  const navigate = useNavigate();
  return (
    <section id="services" className="py-20 lg:py-28 bg-cream-100" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="text-center max-w-2xl mx-auto mb-16">
          <motion.span variants={fadeUp} className="text-gold font-dm font-semibold text-sm tracking-wider uppercase">Our Services</motion.span>
          <motion.h2 variants={fadeUp} className="font-playfair text-3xl md:text-4xl text-charcoal mt-4 mb-6">Comprehensive Digital Solutions</motion.h2>
          <motion.div variants={fadeUp} className="section-divider mx-auto mb-6" />
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <motion.div key={index} variants={fadeUp} className="card-elegant" data-testid={`service-card-${index}`}>
              <div className="service-icon-elegant mb-5"><service.icon className="w-6 h-6 text-gold" /></div>
              <h3 className="font-playfair text-xl text-charcoal mb-3">{service.title}</h3>
              <p className="font-dm text-charcoal-muted text-sm leading-relaxed mb-4">{service.description}</p>
              <button onClick={() => navigate('/services')} className="font-dm text-sm text-navy font-medium flex items-center gap-2 group">
                Learn More<ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

const ContactForm = ({ showNewsletter = true }) => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', service: '', message: '' });
  const [newsletter, setNewsletter] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Thank you! We\'ll get back to you within 24 hours.');
      setFormData({ name: '', email: '', phone: '', service: '', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong.');
    } finally { setIsSubmitting(false); }
  };

  const handleNewsletter = async (e) => {
    e.preventDefault();
    setIsSubscribing(true);
    try {
      await axios.post(`${API}/newsletter`, { email: newsletter });
      toast.success('Successfully subscribed!');
      setNewsletter('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong.');
    } finally { setIsSubscribing(false); }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="lg:col-span-2">
        <motion.span variants={fadeUp} className="text-gold font-dm font-semibold text-sm tracking-wider uppercase">Contact Us</motion.span>
        <motion.h2 variants={fadeUp} className="font-playfair text-3xl md:text-4xl text-charcoal mt-4 mb-6">Let's Discuss Your Project</motion.h2>
        <motion.div variants={fadeUp} className="section-divider mb-6" />
        <motion.p variants={fadeUp} className="font-dm text-charcoal-muted leading-relaxed mb-8">Ready to take your business to the next level? Get in touch for a free consultation.</motion.p>
        <motion.div variants={staggerContainer} className="space-y-5 mb-10">
          <motion.div variants={fadeUp} className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-lg bg-cream-200 flex items-center justify-center flex-shrink-0"><Mail className="w-5 h-5 text-navy" /></div>
            <div><p className="font-dm text-xs text-charcoal-muted uppercase tracking-wider mb-1">Email</p><p className="font-dm text-charcoal font-medium">info@creativeinfotech.net</p></div>
          </motion.div>
          <motion.div variants={fadeUp} className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-lg bg-cream-200 flex items-center justify-center flex-shrink-0"><MapPin className="w-5 h-5 text-navy" /></div>
            <div><p className="font-dm text-xs text-charcoal-muted uppercase tracking-wider mb-1">Address</p><p className="font-dm text-charcoal font-medium">Gujarat, India</p></div>
          </motion.div>
        </motion.div>
        {showNewsletter && (
          <motion.div variants={fadeUp} className="bg-cream-100 p-6 rounded-lg">
            <h3 className="font-playfair text-lg text-charcoal mb-2">Stay Updated</h3>
            <p className="font-dm text-sm text-charcoal-muted mb-4">Subscribe for marketing tips and insights.</p>
            <form onSubmit={handleNewsletter} className="flex gap-3">
              <input type="email" value={newsletter} onChange={(e) => setNewsletter(e.target.value)} placeholder="Enter your email" className="input-elegant flex-1 py-3 text-sm" required data-testid="newsletter-input" />
              <button type="submit" disabled={isSubscribing} className="btn-gold px-5 py-3 disabled:opacity-50" data-testid="newsletter-submit">
                {isSubscribing ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </form>
          </motion.div>
        )}
      </motion.div>
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="lg:col-span-3">
        <div className="bg-cream-50 border border-gray-100 p-8 lg:p-10 rounded-lg">
          <h3 className="font-playfair text-2xl text-charcoal mb-2">Send us a message</h3>
          <p className="font-dm text-sm text-charcoal-muted mb-8">Fill out the form and we'll get back to you shortly.</p>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid md:grid-cols-2 gap-5">
              <div><label className="font-dm text-sm text-charcoal font-medium mb-2 block">Full Name *</label><input type="text" name="name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="input-elegant" required data-testid="contact-name" /></div>
              <div><label className="font-dm text-sm text-charcoal font-medium mb-2 block">Email *</label><input type="email" name="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="input-elegant" required data-testid="contact-email" /></div>
            </div>
            <div><label className="font-dm text-sm text-charcoal font-medium mb-2 block">Service Required *</label>
              <select name="service" value={formData.service} onChange={(e) => setFormData({...formData, service: e.target.value})} className="input-elegant" required data-testid="contact-service">
                <option value="">Select a service</option>
                <option value="seo">SEO & Search Marketing</option>
                <option value="social">Social Media Marketing</option>
                <option value="content">Content Marketing</option>
                <option value="ppc">PPC & Paid Advertising</option>
                <option value="web">Web Development</option>
                <option value="branding">Branding & Design</option>
              </select>
            </div>
            <div><label className="font-dm text-sm text-charcoal font-medium mb-2 block">Project Details *</label><textarea name="message" value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} rows={5} className="input-elegant resize-none" required data-testid="contact-message" /></div>
            <button type="submit" disabled={isSubmitting} className="btn-primary w-full flex items-center justify-center gap-3 disabled:opacity-50" data-testid="contact-submit">
              {isSubmitting ? <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />Sending...</> : <><CheckCircle className="w-5 h-5" />Submit Inquiry</>}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

const ContactSectionHome = () => (
  <section id="contact" className="py-20 lg:py-28 bg-white" data-testid="contact-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-8"><ContactForm /></div>
  </section>
);

// ============ PAGES ============

const Home = () => (
  <div className="min-h-screen bg-cream-50" data-testid="home-page">
    <Navbar /><HeroSection /><AboutSectionHome /><ServicesSectionHome /><ContactSectionHome /><Footer />
  </div>
);

const AboutPage = () => {
  const values = [
    { icon: Lightbulb, title: 'Innovative Solutions', description: 'Fresh ideas tailored for your business growth.' },
    { icon: Rocket, title: 'Results-Driven', description: 'Measurable outcomes that impact your bottom line.' },
    { icon: Heart, title: 'Client-Centric', description: 'Your success is our priority.' },
    { icon: Sparkles, title: 'Creative Excellence', description: 'Unique campaigns that make your brand stand out.' }
  ];
  return (
    <div className="min-h-screen bg-cream-50 pt-20"><Navbar />
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-3xl">
            <motion.span variants={fadeUp} className="text-gold font-dm font-semibold text-sm tracking-wider uppercase">About Us</motion.span>
            <motion.h1 variants={fadeUp} className="font-playfair text-4xl md:text-5xl text-charcoal mt-4 mb-6">Your Growth is Our Mission</motion.h1>
            <motion.div variants={fadeUp} className="section-divider mb-6" />
            <motion.p variants={fadeUp} className="font-dm text-lg text-charcoal-muted leading-relaxed">Creative Infotech is a dynamic digital marketing agency based in Gujarat, India, established in 2024.</motion.p>
          </motion.div>
        </div>
      </section>
      <section className="py-20 lg:py-28 bg-cream-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div key={index} variants={fadeUp} className="card-elegant text-center">
                <div className="w-14 h-14 rounded-lg bg-gold/10 flex items-center justify-center mx-auto mb-4"><value.icon className="w-7 h-7 text-gold" /></div>
                <h3 className="font-playfair text-xl text-charcoal mb-2">{value.title}</h3>
                <p className="font-dm text-sm text-charcoal-muted">{value.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

const ServicesPage = () => (
  <div className="min-h-screen bg-cream-50 pt-20"><Navbar />
    <section className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-3xl">
          <motion.span variants={fadeUp} className="text-gold font-dm font-semibold text-sm tracking-wider uppercase">Our Services</motion.span>
          <motion.h1 variants={fadeUp} className="font-playfair text-4xl md:text-5xl text-charcoal mt-4 mb-6">Comprehensive Digital Solutions</motion.h1>
          <motion.div variants={fadeUp} className="section-divider mb-6" />
        </motion.div>
      </div>
    </section>
    <section className="py-20 lg:py-28 bg-cream-100">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <motion.div key={index} variants={fadeUp} className="card-elegant">
              <div className="service-icon-elegant mb-5"><service.icon className="w-6 h-6 text-gold" /></div>
              <h3 className="font-playfair text-xl text-charcoal mb-3">{service.title}</h3>
              <p className="font-dm text-charcoal-muted text-sm leading-relaxed">{service.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
    <section className="py-20 lg:py-28 bg-navy">
      <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <h2 className="font-playfair text-3xl md:text-4xl text-white mb-6">Ready to Get Started?</h2>
        <p className="font-dm text-white/80 mb-8">Let's discuss how our services can help your business grow.</p>
        <Link to="/contact" className="btn-gold inline-flex items-center gap-2">Get a Free Quote<ArrowRight className="w-4 h-4" /></Link>
      </div>
    </section>
    <Footer />
  </div>
);

const ContactPage = () => (
  <div className="min-h-screen bg-cream-50 pt-20"><Navbar />
    <section className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-3xl">
          <motion.span variants={fadeUp} className="text-gold font-dm font-semibold text-sm tracking-wider uppercase">Contact Us</motion.span>
          <motion.h1 variants={fadeUp} className="font-playfair text-4xl md:text-5xl text-charcoal mt-4 mb-6">Let's Start a Conversation</motion.h1>
          <motion.div variants={fadeUp} className="section-divider mb-6" />
        </motion.div>
      </div>
    </section>
    <section className="py-20 lg:py-28 bg-cream-100"><div className="max-w-7xl mx-auto px-6 lg:px-8"><ContactForm /></div></section>
    <Footer />
  </div>
);

// ============ CAREERS PAGE ============

const CareersPage = () => {
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [viewJob, setViewJob] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', position: '', experience: '', linkedin: '', portfolio: '', cover_letter: '' });
  const [resume, setResume] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Application Tracking State
  const [showTracker, setShowTracker] = useState(false);
  const [trackEmail, setTrackEmail] = useState('');
  const [trackedApplications, setTrackedApplications] = useState([]);
  const [isTracking, setIsTracking] = useState(false);
  const [trackError, setTrackError] = useState('');

  useEffect(() => {
    axios.get(`${API}/jobs`).then(res => setJobs(res.data)).catch(() => {});
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'application/pdf': ['.pdf'], 'application/msword': ['.doc'], 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'] },
    maxSize: 5242880,
    onDrop: (files) => setResume(files[0])
  });

  const handleApply = (job = null) => {
    setSelectedJob(job);
    setFormData({ ...formData, position: job?.title || '' });
    setViewJob(null);
    setShowForm(true);
  };

  const handleViewDetails = (job) => {
    setViewJob(job);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const data = new FormData();
      Object.keys(formData).forEach(key => data.append(key, formData[key]));
      if (selectedJob) data.append('job_id', selectedJob.id);
      if (resume) data.append('resume', resume);
      
      const response = await axios.post(`${API}/careers/apply`, data);
      toast.success(`Application submitted! Your Application ID: ${response.data.application_id}`);
      setShowForm(false);
      setFormData({ name: '', email: '', phone: '', position: '', experience: '', linkedin: '', portfolio: '', cover_letter: '' });
      setResume(null);
      setSelectedJob(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application.');
    } finally { setIsSubmitting(false); }
  };

  const handleTrackApplication = async (e) => {
    e.preventDefault();
    setIsTracking(true);
    setTrackError('');
    try {
      const response = await axios.post(`${API}/careers/track`, { email: trackEmail });
      setTrackedApplications(response.data.applications);
    } catch (error) {
      setTrackError(error.response?.data?.detail || 'No applications found with this email.');
      setTrackedApplications([]);
    } finally { setIsTracking(false); }
  };

  const getStatusColor = (color) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-700 border-blue-200',
      yellow: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      green: 'bg-green-100 text-green-700 border-green-200',
      purple: 'bg-purple-100 text-purple-700 border-purple-200',
      red: 'bg-red-100 text-red-700 border-red-200'
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="min-h-screen bg-cream-50 pt-20"><Navbar />
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-3xl">
            <motion.span variants={fadeUp} className="text-gold font-dm font-semibold text-sm tracking-wider uppercase">Careers</motion.span>
            <motion.h1 variants={fadeUp} className="font-playfair text-4xl md:text-5xl text-charcoal mt-4 mb-6">Join Our Team</motion.h1>
            <motion.div variants={fadeUp} className="section-divider mb-6" />
            <motion.p variants={fadeUp} className="font-dm text-lg text-charcoal-muted leading-relaxed">
              Be part of a dynamic team that's shaping the future of digital marketing. We're always looking for talented individuals who share our passion for creativity and excellence.
            </motion.p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-cream-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-12">
            <h2 className="font-playfair text-3xl text-charcoal">Open Positions</h2>
            <div className="flex gap-3">
              <button onClick={() => setShowTracker(true)} className="btn-outline flex items-center gap-2" data-testid="track-application-btn">
                <Search className="w-4 h-4" />Track Application
              </button>
              <button onClick={() => handleApply()} className="btn-gold flex items-center gap-2" data-testid="general-apply-btn">
                <Upload className="w-4 h-4" />General Application
              </button>
            </div>
          </div>

          {jobs.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-lg border border-gray-100">
              <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="font-playfair text-xl text-charcoal mb-2">No Open Positions</h3>
              <p className="font-dm text-charcoal-muted mb-6">We don't have any openings right now, but we're always interested in hearing from talented people.</p>
              <button onClick={() => handleApply()} className="btn-primary">Submit General Application</button>
            </div>
          ) : (
            <div className="space-y-6">
              {jobs.map((job) => (
                <motion.div key={job.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-white rounded-lg border border-gray-100 p-6 hover:shadow-lg transition-shadow">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <h3 className="font-playfair text-xl text-charcoal mb-2">{job.title}</h3>
                      <div className="flex flex-wrap gap-3 text-sm">
                        <span className="flex items-center gap-1 text-charcoal-muted"><Building className="w-4 h-4" />{job.department}</span>
                        <span className="flex items-center gap-1 text-charcoal-muted"><MapPin className="w-4 h-4" />{job.location}</span>
                        <span className="flex items-center gap-1 text-charcoal-muted"><Clock className="w-4 h-4" />{job.type}</span>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <button onClick={() => handleViewDetails(job)} className="btn-outline whitespace-nowrap flex items-center gap-2" data-testid={`view-btn-${job.id}`}>
                        <Eye className="w-4 h-4" />View Details
                      </button>
                      <button onClick={() => handleApply(job)} className="btn-primary whitespace-nowrap" data-testid={`apply-btn-${job.id}`}>Apply Now</button>
                    </div>
                  </div>
                  <p className="font-dm text-charcoal-muted mt-4 line-clamp-2">{job.description}</p>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Job Details Modal */}
      {viewJob && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white">
              <div>
                <h2 className="font-playfair text-2xl text-charcoal">{viewJob.title}</h2>
                <div className="flex flex-wrap gap-3 text-sm mt-2">
                  <span className="flex items-center gap-1 text-charcoal-muted"><Building className="w-4 h-4" />{viewJob.department}</span>
                  <span className="flex items-center gap-1 text-charcoal-muted"><MapPin className="w-4 h-4" />{viewJob.location}</span>
                  <span className="flex items-center gap-1 text-charcoal-muted"><Clock className="w-4 h-4" />{viewJob.type}</span>
                </div>
              </div>
              <button onClick={() => setViewJob(null)} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <h3 className="font-playfair text-lg text-charcoal mb-3">Job Description</h3>
                <p className="font-dm text-charcoal-muted leading-relaxed whitespace-pre-wrap">{viewJob.description}</p>
              </div>
              
              {viewJob.requirements && viewJob.requirements.length > 0 && (
                <div>
                  <h3 className="font-playfair text-lg text-charcoal mb-3">Requirements</h3>
                  <ul className="space-y-2">
                    {viewJob.requirements.map((req, i) => (
                      <li key={i} className="font-dm text-charcoal-muted flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                        {req}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {viewJob.responsibilities && viewJob.responsibilities.length > 0 && (
                <div>
                  <h3 className="font-playfair text-lg text-charcoal mb-3">Responsibilities</h3>
                  <ul className="space-y-2">
                    {viewJob.responsibilities.map((resp, i) => (
                      <li key={i} className="font-dm text-charcoal-muted flex items-start gap-2">
                        <ArrowRight className="w-5 h-5 text-navy flex-shrink-0 mt-0.5" />
                        {resp}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              <div className="pt-4 border-t border-gray-100 flex gap-4">
                <button onClick={() => setViewJob(null)} className="btn-outline flex-1">Close</button>
                <button onClick={() => handleApply(viewJob)} className="btn-primary flex-1 flex items-center justify-center gap-2">
                  <Send className="w-4 h-4" />Apply for this Position
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Application Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white">
              <div>
                <h2 className="font-playfair text-2xl text-charcoal">Apply for Position</h2>
                {selectedJob && <p className="text-sm text-charcoal-muted">{selectedJob.title}</p>}
              </div>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                <div><label className="font-dm text-sm text-charcoal font-medium mb-2 block">Full Name *</label><input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="input-elegant" required data-testid="career-name" /></div>
                <div><label className="font-dm text-sm text-charcoal font-medium mb-2 block">Email *</label><input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="input-elegant" required data-testid="career-email" /></div>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <div><label className="font-dm text-sm text-charcoal font-medium mb-2 block">Phone *</label><input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="input-elegant" required data-testid="career-phone" /></div>
                <div><label className="font-dm text-sm text-charcoal font-medium mb-2 block">Position Applying For *</label><input type="text" value={formData.position} onChange={(e) => setFormData({...formData, position: e.target.value})} className="input-elegant" required data-testid="career-position" /></div>
              </div>
              <div><label className="font-dm text-sm text-charcoal font-medium mb-2 block">Experience *</label>
                <select value={formData.experience} onChange={(e) => setFormData({...formData, experience: e.target.value})} className="input-elegant" required data-testid="career-experience">
                  <option value="">Select experience level</option>
                  <option value="fresher">Fresher (0-1 years)</option>
                  <option value="junior">Junior (1-3 years)</option>
                  <option value="mid">Mid-level (3-5 years)</option>
                  <option value="senior">Senior (5-8 years)</option>
                  <option value="expert">Expert (8+ years)</option>
                </select>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <div><label className="font-dm text-sm text-charcoal font-medium mb-2 block">LinkedIn Profile</label><input type="url" value={formData.linkedin} onChange={(e) => setFormData({...formData, linkedin: e.target.value})} placeholder="https://linkedin.com/in/..." className="input-elegant" data-testid="career-linkedin" /></div>
                <div><label className="font-dm text-sm text-charcoal font-medium mb-2 block">Portfolio URL</label><input type="url" value={formData.portfolio} onChange={(e) => setFormData({...formData, portfolio: e.target.value})} placeholder="https://..." className="input-elegant" data-testid="career-portfolio" /></div>
              </div>
              <div>
                <label className="font-dm text-sm text-charcoal font-medium mb-2 block">Resume *</label>
                <div {...getRootProps()} className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${isDragActive ? 'border-gold bg-gold/5' : 'border-gray-200 hover:border-gold'}`}>
                  <input {...getInputProps()} data-testid="career-resume" />
                  {resume ? (
                    <div className="flex items-center justify-center gap-2 text-charcoal"><FileText className="w-5 h-5 text-gold" /><span>{resume.name}</span><button type="button" onClick={(e) => { e.stopPropagation(); setResume(null); }} className="text-red-500 hover:text-red-700"><X className="w-4 h-4" /></button></div>
                  ) : (
                    <div><Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" /><p className="font-dm text-sm text-charcoal-muted">Drag & drop your resume or <span className="text-gold">browse</span></p><p className="font-dm text-xs text-gray-400 mt-1">PDF, DOC, DOCX (max 5MB)</p></div>
                  )}
                </div>
              </div>
              <div><label className="font-dm text-sm text-charcoal font-medium mb-2 block">Cover Letter</label><textarea value={formData.cover_letter} onChange={(e) => setFormData({...formData, cover_letter: e.target.value})} rows={4} placeholder="Tell us why you'd be a great fit..." className="input-elegant resize-none" data-testid="career-cover" /></div>
              <div className="flex gap-4 pt-4">
                <button type="button" onClick={() => setShowForm(false)} className="btn-outline flex-1">Cancel</button>
                <button type="submit" disabled={isSubmitting || !resume} className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50" data-testid="career-submit">
                  {isSubmitting ? <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />Submitting...</> : <><Send className="w-4 h-4" />Submit Application</>}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Application Tracker Modal */}
      {showTracker && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white">
              <div>
                <h2 className="font-playfair text-2xl text-charcoal">Track Your Application</h2>
                <p className="font-dm text-sm text-charcoal-muted mt-1">Enter your email to check application status</p>
              </div>
              <button onClick={() => { setShowTracker(false); setTrackedApplications([]); setTrackError(''); }} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
            </div>
            <div className="p-6">
              <form onSubmit={handleTrackApplication} className="flex gap-3 mb-6">
                <input
                  type="email"
                  value={trackEmail}
                  onChange={(e) => setTrackEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="input-elegant flex-1"
                  required
                  data-testid="track-email-input"
                />
                <button type="submit" disabled={isTracking} className="btn-primary flex items-center gap-2" data-testid="track-submit-btn">
                  {isTracking ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Search className="w-4 h-4" />}
                  Track
                </button>
              </form>

              {trackError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <p className="font-dm text-sm text-red-600">{trackError}</p>
                </div>
              )}

              {trackedApplications.length > 0 && (
                <div className="space-y-4">
                  <h3 className="font-playfair text-lg text-charcoal">Your Applications ({trackedApplications.length})</h3>
                  {trackedApplications.map((app, index) => (
                    <div key={index} className="bg-cream-50 border border-gray-100 rounded-lg p-5">
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
                        <div>
                          <h4 className="font-playfair text-lg text-charcoal">{app.position}</h4>
                          {app.job_title && <p className="font-dm text-sm text-charcoal-muted">Applied for: {app.job_title}</p>}
                          <p className="font-dm text-xs text-charcoal-muted mt-1">Applied on: {new Date(app.applied_on).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm font-dm font-medium border ${getStatusColor(app.status_color)}`}>
                          {app.status_label}
                        </span>
                      </div>
                      <div className="bg-white rounded-lg p-4 border border-gray-100">
                        <p className="font-dm text-sm text-charcoal-muted">{app.status_description}</p>
                      </div>
                      <p className="font-dm text-xs text-charcoal-muted mt-3">Application ID: {app.application_id}</p>
                    </div>
                  ))}
                </div>
              )}

              {!trackError && trackedApplications.length === 0 && !isTracking && (
                <div className="text-center py-8">
                  <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="font-dm text-charcoal-muted">Enter your email to track your job applications</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
      <Footer />
    </div>
  );
};

// ============ LEGAL PAGES ============

const PrivacyPolicyPage = () => (
  <div className="min-h-screen bg-cream-50 pt-20"><Navbar />
    <section className="py-20 lg:py-28 bg-white">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <Link to="/" className="font-dm text-sm text-navy flex items-center gap-2 hover:text-gold transition-colors mb-6"><ArrowLeft className="w-4 h-4" />Back to Home</Link>
        <h1 className="font-playfair text-4xl md:text-5xl text-charcoal mb-4">Privacy Policy</h1>
        <p className="font-dm text-charcoal-muted mb-8">Last updated: December 2024</p>
        <div className="space-y-8 font-dm text-charcoal-muted leading-relaxed">
          <div><h2 className="font-playfair text-2xl text-charcoal mb-4">1. Information We Collect</h2><p>At Creative Infotech, we collect information you provide directly to us through contact forms, newsletter subscriptions, and job applications.</p></div>
          <div><h2 className="font-playfair text-2xl text-charcoal mb-4">2. How We Use Your Information</h2><p>We use collected information to respond to inquiries, send marketing communications (with consent), and improve our services.</p></div>
          <div><h2 className="font-playfair text-2xl text-charcoal mb-4">3. Contact Us</h2><p><strong className="text-charcoal">Email:</strong> info@creativeinfotech.net<br /><strong className="text-charcoal">Location:</strong> Gujarat, India</p></div>
        </div>
      </div>
    </section>
    <Footer />
  </div>
);

const TermsOfServicePage = () => (
  <div className="min-h-screen bg-cream-50 pt-20"><Navbar />
    <section className="py-20 lg:py-28 bg-white">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <Link to="/" className="font-dm text-sm text-navy flex items-center gap-2 hover:text-gold transition-colors mb-6"><ArrowLeft className="w-4 h-4" />Back to Home</Link>
        <h1 className="font-playfair text-4xl md:text-5xl text-charcoal mb-4">Terms of Service</h1>
        <p className="font-dm text-charcoal-muted mb-8">Last updated: December 2024</p>
        <div className="space-y-8 font-dm text-charcoal-muted leading-relaxed">
          <div><h2 className="font-playfair text-2xl text-charcoal mb-4">1. Acceptance of Terms</h2><p>By accessing Creative Infotech services, you accept these Terms of Service.</p></div>
          <div><h2 className="font-playfair text-2xl text-charcoal mb-4">2. Services</h2><p>We provide digital marketing services including SEO, Social Media Marketing, Content Marketing, PPC, Web Development, and Branding.</p></div>
          <div><h2 className="font-playfair text-2xl text-charcoal mb-4">3. Contact</h2><p><strong className="text-charcoal">Email:</strong> info@creativeinfotech.net<br /><strong className="text-charcoal">Location:</strong> Gujarat, India</p></div>
        </div>
      </div>
    </section>
    <Footer />
  </div>
);

// ============ ADMIN PAGES ============

const AdminLogin = () => {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [adminExists, setAdminExists] = useState(true);
  const [formData, setFormData] = useState({ name: '', email: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);

  useEffect(() => {
    // Check if admin already exists
    axios.get(`${API}/admin/check-exists`)
      .then(res => {
        setAdminExists(res.data.exists);
        setCheckingAdmin(false);
      })
      .catch(() => {
        setAdminExists(true); // Assume exists on error
        setCheckingAdmin(false);
      });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      if (isRegister && !adminExists) {
        await register(formData.name, formData.email, formData.password);
      } else {
        await login(formData.email, formData.password);
      }
      navigate('/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally { setIsLoading(false); }
  };

  if (checkingAdmin) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-gold/30 border-t-gold rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy flex items-center justify-center p-4">
      <div className="bg-white rounded-lg p-8 w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-12 h-12 rounded bg-gold flex items-center justify-center"><span className="font-playfair font-bold text-white text-xl">CI</span></div>
          <div><span className="font-playfair font-semibold text-xl text-charcoal block">Admin Portal</span><span className="text-xs text-charcoal-muted">Creative Infotech</span></div>
        </div>
        <h2 className="font-playfair text-2xl text-charcoal mb-6 text-center">
          {!adminExists && isRegister ? 'Create Admin Account' : 'Welcome Back'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {!adminExists && isRegister && (
            <div>
              <label className="font-dm text-sm text-charcoal font-medium mb-2 block">Full Name</label>
              <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="input-elegant" required />
            </div>
          )}
          <div><label className="font-dm text-sm text-charcoal font-medium mb-2 block">Email</label><input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="input-elegant" required data-testid="admin-email" /></div>
          <div><label className="font-dm text-sm text-charcoal font-medium mb-2 block">Password</label><input type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} className="input-elegant" required data-testid="admin-password" /></div>
          <button type="submit" disabled={isLoading} className="btn-primary w-full" data-testid="admin-login-btn">
            {isLoading ? 'Please wait...' : (!adminExists && isRegister ? 'Create Account' : 'Sign In')}
          </button>
        </form>
        {!adminExists && (
          <p className="text-center mt-6 font-dm text-sm text-charcoal-muted">
            {isRegister ? 'Already have an account?' : "First time? Create admin account"}{' '}
            <button onClick={() => setIsRegister(!isRegister)} className="text-gold hover:underline">{isRegister ? 'Sign In' : 'Register'}</button>
          </p>
        )}
      </div>
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const { admin, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-gold/30 border-t-gold rounded-full animate-spin"></div></div>;
  if (!admin) return <Navigate to="/admin/login" replace />;
  return children;
};

const AdminLayout = ({ children }) => {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
    { icon: BarChart3, label: 'Analytics', path: '/admin/analytics' },
    { icon: MessageSquare, label: 'Contact Inquiries', path: '/admin/contacts' },
    { icon: Newspaper, label: 'Newsletter', path: '/admin/newsletter' },
    { icon: Briefcase, label: 'Job Postings', path: '/admin/jobs' },
    { icon: FileUser, label: 'Applications', path: '/admin/applications' },
    { icon: Clock, label: 'Interviews', path: '/admin/interviews' },
    { icon: FileText, label: 'Offer Letter', path: '/admin/offer-letter' },
    { icon: Mail, label: 'Email Logs', path: '/admin/email-logs' },
    { icon: UserPlus, label: 'Pre-Joining', path: '/admin/prejoining' },
    { icon: GraduationCap, label: 'Training Portal', path: '/admin/training' },
    { icon: Settings, label: 'CMS', path: '/admin/cms' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className={`bg-navy text-white ${sidebarOpen ? 'w-64' : 'w-20'} transition-all duration-300 flex flex-col`}>
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded bg-gold flex items-center justify-center flex-shrink-0"><span className="font-playfair font-bold text-white">CI</span></div>
            {sidebarOpen && <span className="font-playfair font-semibold">Admin Panel</span>}
          </div>
        </div>
        <nav className="flex-1 p-4">
          {menuItems.map((item) => (
            <Link key={item.path} to={item.path} className="flex items-center gap-3 px-3 py-3 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors mb-1">
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span className="font-dm text-sm">{item.label}</span>}
            </Link>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <button onClick={() => { logout(); navigate('/admin/login'); }} className="flex items-center gap-3 px-3 py-3 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors w-full">
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span className="font-dm text-sm">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-gray-500 hover:text-gray-700"><Menu className="w-6 h-6" /></button>
          <div className="flex items-center gap-3">
            <span className="font-dm text-sm text-charcoal-muted">Welcome, {admin?.name}</span>
            <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center"><span className="text-white font-bold text-sm">{admin?.name?.charAt(0)}</span></div>
          </div>
        </header>
        <main className="flex-1 p-6 overflow-auto">{children}</main>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/dashboard`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setStats(res.data)).catch(() => {});
  }, [token]);

  const statCards = [
    { label: 'Contact Inquiries', value: stats?.contacts?.total || 0, new: stats?.contacts?.new || 0, icon: MessageSquare, color: 'bg-blue-500' },
    { label: 'Newsletter Subscribers', value: stats?.newsletter?.total || 0, icon: Newspaper, color: 'bg-green-500' },
    { label: 'Job Applications', value: stats?.applications?.total || 0, new: stats?.applications?.new || 0, icon: FileUser, color: 'bg-purple-500' },
    { label: 'Active Jobs', value: stats?.jobs?.active || 0, icon: Briefcase, color: 'bg-orange-500' },
  ];

  return (
    <div>
      <h1 className="font-playfair text-2xl text-charcoal mb-6">Dashboard</h1>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, i) => (
          <div key={i} className="bg-white rounded-lg p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 rounded-lg ${stat.color} flex items-center justify-center`}><stat.icon className="w-6 h-6 text-white" /></div>
              {stat.new > 0 && <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">{stat.new} new</span>}
            </div>
            <p className="font-playfair text-3xl text-charcoal mb-1">{stat.value}</p>
            <p className="font-dm text-sm text-charcoal-muted">{stat.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const AdminContacts = () => {
  const { token } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/contacts`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { setContacts(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, [token]);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/contacts/${id}/status?status=${status}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setContacts(contacts.map(c => c.id === id ? {...c, status} : c));
      toast.success('Status updated');
    } catch { toast.error('Failed to update status'); }
  };

  const deleteContact = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setContacts(contacts.filter(c => c.id !== id));
      toast.success('Contact deleted');
    } catch { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-gold/30 border-t-gold rounded-full animate-spin"></div></div>;

  return (
    <div>
      <h1 className="font-playfair text-2xl text-charcoal mb-6">Contact Inquiries</h1>
      <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr><th className="px-6 py-3 text-left font-dm text-sm font-medium text-charcoal-muted">Name</th><th className="px-6 py-3 text-left font-dm text-sm font-medium text-charcoal-muted">Email</th><th className="px-6 py-3 text-left font-dm text-sm font-medium text-charcoal-muted">Service</th><th className="px-6 py-3 text-left font-dm text-sm font-medium text-charcoal-muted">Status</th><th className="px-6 py-3 text-left font-dm text-sm font-medium text-charcoal-muted">Date</th><th className="px-6 py-3"></th></tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {contacts.map((contact) => (
              <tr key={contact.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 font-dm text-sm text-charcoal">{contact.name}</td>
                <td className="px-6 py-4 font-dm text-sm text-charcoal-muted">{contact.email}</td>
                <td className="px-6 py-4 font-dm text-sm text-charcoal-muted capitalize">{contact.service}</td>
                <td className="px-6 py-4">
                  <select value={contact.status} onChange={(e) => updateStatus(contact.id, e.target.value)} className="text-sm border rounded px-2 py-1">
                    <option value="new">New</option><option value="contacted">Contacted</option><option value="closed">Closed</option>
                  </select>
                </td>
                <td className="px-6 py-4 font-dm text-sm text-charcoal-muted">{new Date(contact.created_at).toLocaleDateString()}</td>
                <td className="px-6 py-4"><button onClick={() => deleteContact(contact.id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {contacts.length === 0 && <p className="text-center py-12 text-charcoal-muted">No contacts yet</p>}
      </div>
    </div>
  );
};

const AdminNewsletter = () => {
  const { token } = useAuth();
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/newsletter`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { setSubscribers(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, [token]);

  const deleteSubscriber = async (id) => {
    if (!window.confirm('Remove this subscriber?')) return;
    try {
      await axios.delete(`${API}/admin/newsletter/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSubscribers(subscribers.filter(s => s.id !== id));
      toast.success('Subscriber removed');
    } catch { toast.error('Failed to remove'); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-gold/30 border-t-gold rounded-full animate-spin"></div></div>;

  return (
    <div>
      <h1 className="font-playfair text-2xl text-charcoal mb-6">Newsletter Subscribers ({subscribers.length})</h1>
      <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50"><tr><th className="px-6 py-3 text-left font-dm text-sm font-medium text-charcoal-muted">Email</th><th className="px-6 py-3 text-left font-dm text-sm font-medium text-charcoal-muted">Subscribed</th><th className="px-6 py-3"></th></tr></thead>
          <tbody className="divide-y divide-gray-100">
            {subscribers.map((sub) => (
              <tr key={sub.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 font-dm text-sm text-charcoal">{sub.email}</td>
                <td className="px-6 py-4 font-dm text-sm text-charcoal-muted">{new Date(sub.subscribed_at).toLocaleDateString()}</td>
                <td className="px-6 py-4"><button onClick={() => deleteSubscriber(sub.id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {subscribers.length === 0 && <p className="text-center py-12 text-charcoal-muted">No subscribers yet</p>}
      </div>
    </div>
  );
};

const AdminJobs = () => {
  const { token } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editJob, setEditJob] = useState(null);
  const [formData, setFormData] = useState({ title: '', department: '', location: '', type: 'Full-time', description: '', requirements: '', responsibilities: '', is_active: true });

  useEffect(() => {
    axios.get(`${API}/admin/jobs`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { setJobs(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = { ...formData, requirements: formData.requirements.split('\n').filter(r => r.trim()), responsibilities: formData.responsibilities.split('\n').filter(r => r.trim()) };
    try {
      if (editJob) {
        const res = await axios.put(`${API}/admin/jobs/${editJob.id}`, data, { headers: { Authorization: `Bearer ${token}` } });
        setJobs(jobs.map(j => j.id === editJob.id ? res.data : j));
        toast.success('Job updated');
      } else {
        const res = await axios.post(`${API}/admin/jobs`, data, { headers: { Authorization: `Bearer ${token}` } });
        setJobs([res.data, ...jobs]);
        toast.success('Job created');
      }
      setShowForm(false);
      setEditJob(null);
      setFormData({ title: '', department: '', location: '', type: 'Full-time', description: '', requirements: '', responsibilities: '', is_active: true });
    } catch { toast.error('Failed to save job'); }
  };

  const deleteJob = async (id) => {
    if (!window.confirm('Delete this job?')) return;
    try {
      await axios.delete(`${API}/admin/jobs/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setJobs(jobs.filter(j => j.id !== id));
      toast.success('Job deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const openEdit = (job) => {
    setEditJob(job);
    setFormData({ ...job, requirements: job.requirements.join('\n'), responsibilities: job.responsibilities.join('\n') });
    setShowForm(true);
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-gold/30 border-t-gold rounded-full animate-spin"></div></div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="font-playfair text-2xl text-charcoal">Job Postings</h1>
        <button onClick={() => { setEditJob(null); setFormData({ title: '', department: '', location: '', type: 'Full-time', description: '', requirements: '', responsibilities: '', is_active: true }); setShowForm(true); }} className="btn-primary flex items-center gap-2"><Plus className="w-4 h-4" />Add Job</button>
      </div>
      <div className="space-y-4">
        {jobs.map((job) => (
          <div key={job.id} className="bg-white rounded-lg border border-gray-100 p-6">
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center gap-3 mb-2"><h3 className="font-playfair text-xl text-charcoal">{job.title}</h3><span className={`text-xs px-2 py-1 rounded-full ${job.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{job.is_active ? 'Active' : 'Inactive'}</span></div>
                <p className="font-dm text-sm text-charcoal-muted">{job.department} • {job.location} • {job.type}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => openEdit(job)} className="text-blue-500 hover:text-blue-700"><Edit className="w-4 h-4" /></button>
                <button onClick={() => deleteJob(job.id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
        {jobs.length === 0 && <p className="text-center py-12 text-charcoal-muted bg-white rounded-lg">No jobs posted yet</p>}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center"><h2 className="font-playfair text-xl text-charcoal">{editJob ? 'Edit Job' : 'New Job'}</h2><button onClick={() => setShowForm(false)}><X className="w-6 h-6" /></button></div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div><label className="font-dm text-sm text-charcoal font-medium mb-2 block">Job Title *</label><input type="text" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="input-elegant" required /></div>
                <div><label className="font-dm text-sm text-charcoal font-medium mb-2 block">Department *</label><input type="text" value={formData.department} onChange={(e) => setFormData({...formData, department: e.target.value})} className="input-elegant" required /></div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div><label className="font-dm text-sm text-charcoal font-medium mb-2 block">Location *</label><input type="text" value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} className="input-elegant" required /></div>
                <div><label className="font-dm text-sm text-charcoal font-medium mb-2 block">Type *</label>
                  <select value={formData.type} onChange={(e) => setFormData({...formData, type: e.target.value})} className="input-elegant">
                    <option>Full-time</option><option>Part-time</option><option>Contract</option><option>Internship</option>
                  </select>
                </div>
              </div>
              <div><label className="font-dm text-sm text-charcoal font-medium mb-2 block">Description *</label><textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={3} className="input-elegant resize-none" required /></div>
              <div><label className="font-dm text-sm text-charcoal font-medium mb-2 block">Requirements (one per line)</label><textarea value={formData.requirements} onChange={(e) => setFormData({...formData, requirements: e.target.value})} rows={3} className="input-elegant resize-none" placeholder="3+ years experience&#10;Bachelor's degree" /></div>
              <div><label className="font-dm text-sm text-charcoal font-medium mb-2 block">Responsibilities (one per line)</label><textarea value={formData.responsibilities} onChange={(e) => setFormData({...formData, responsibilities: e.target.value})} rows={3} className="input-elegant resize-none" /></div>
              <div className="flex items-center gap-2"><input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({...formData, is_active: e.target.checked})} id="active" /><label htmlFor="active" className="font-dm text-sm text-charcoal">Active (visible on careers page)</label></div>
              <div className="flex gap-4 pt-4"><button type="button" onClick={() => setShowForm(false)} className="btn-outline flex-1">Cancel</button><button type="submit" className="btn-primary flex-1">{editJob ? 'Update Job' : 'Create Job'}</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminApplications = () => {
  const { token } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [activeTab, setActiveTab] = useState('applications'); // applications, templates
  const [templates, setTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  
  // Interview scheduling
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [schedulingApp, setSchedulingApp] = useState(null);
  const [interviewDate, setInterviewDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [interviewNotes, setInterviewNotes] = useState('');
  const [scheduling, setScheduling] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/applications`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { setApplications(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (activeTab === 'templates') {
      axios.get(`${API}/admin/application-email-templates`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setTemplates(res.data)).catch(() => {});
    }
  }, [activeTab, token]);

  const updateStatus = async (id, status, app) => {
    // If selecting interview, show scheduling modal
    if (status === 'interview') {
      setSchedulingApp(app || applications.find(a => a.id === id));
      setShowScheduleModal(true);
      // Set default date to tomorrow
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setInterviewDate(tomorrow.toISOString().split('T')[0]);
      setStartTime('10:00');
      setEndTime('10:30');
      setInterviewNotes('');
      return;
    }
    
    try {
      const res = await axios.put(`${API}/admin/applications/${id}/status?status=${status}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setApplications(applications.map(a => a.id === id ? {...a, status} : a));
      if (res.data.email_sent) {
        toast.success('Status updated & email sent to applicant');
      } else {
        toast.success('Status updated');
      }
    } catch { toast.error('Failed to update status'); }
  };

  const scheduleInterview = async () => {
    if (!schedulingApp || !interviewDate || !startTime || !endTime) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setScheduling(true);
    try {
      await axios.post(`${API}/admin/applications/${schedulingApp.id}/schedule-interview`, {
        interview_date: interviewDate,
        start_time: startTime,
        end_time: endTime,
        notes: interviewNotes
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      setApplications(applications.map(a => a.id === schedulingApp.id ? {
        ...a, 
        status: 'interview',
        interview_scheduled: true,
        interview_date: interviewDate,
        interview_time: `${startTime} - ${endTime}`
      } : a));
      
      setShowScheduleModal(false);
      setSchedulingApp(null);
      toast.success('Interview scheduled & email sent to candidate!');
    } catch { toast.error('Failed to schedule interview'); }
    finally { setScheduling(false); }
  };

  const viewApplication = async (id) => {
    try {
      const res = await axios.get(`${API}/admin/applications/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedApp(res.data);
    } catch { toast.error('Failed to load application'); }
  };

  const downloadResume = (app) => {
    if (!app.resume_data) return;
    const link = document.createElement('a');
    link.href = `data:application/octet-stream;base64,${app.resume_data}`;
    link.download = app.resume_filename || 'resume.pdf';
    link.click();
  };

  const saveTemplate = async () => {
    if (!editingTemplate) return;
    setSavingTemplate(true);
    try {
      await axios.put(`${API}/admin/application-email-templates/${editingTemplate.template_key}`, {
        name: editingTemplate.name,
        subject: editingTemplate.subject,
        body: editingTemplate.body
      }, { headers: { Authorization: `Bearer ${token}` } });
      setTemplates(templates.map(t => t.template_key === editingTemplate.template_key ? {...editingTemplate, is_default: false} : t));
      setEditingTemplate(null);
      toast.success('Template saved');
    } catch { toast.error('Failed to save template'); }
    finally { setSavingTemplate(false); }
  };

  const resetTemplate = async (templateKey) => {
    if (!window.confirm('Reset this template to default?')) return;
    try {
      await axios.post(`${API}/admin/application-email-templates/${templateKey}/reset`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const res = await axios.get(`${API}/admin/application-email-templates`, { headers: { Authorization: `Bearer ${token}` } });
      setTemplates(res.data);
      toast.success('Template reset to default');
    } catch { toast.error('Failed to reset template'); }
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-100 text-blue-700',
      reviewing: 'bg-yellow-100 text-yellow-700',
      shortlisted: 'bg-green-100 text-green-700',
      interview: 'bg-purple-100 text-purple-700',
      rejected: 'bg-red-100 text-red-700',
      hired: 'bg-emerald-100 text-emerald-700'
    };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.new}`}>{status}</span>;
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-gold/30 border-t-gold rounded-full animate-spin"></div></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-playfair text-2xl text-charcoal">Job Applications</h1>
        <div className="flex gap-2">
          <button onClick={() => setActiveTab('applications')} className={`px-4 py-2 rounded-lg font-dm text-sm ${activeTab === 'applications' ? 'bg-navy text-white' : 'bg-gray-100 text-charcoal hover:bg-gray-200'}`}>
            Applications ({applications.length})
          </button>
          <button onClick={() => setActiveTab('templates')} className={`px-4 py-2 rounded-lg font-dm text-sm ${activeTab === 'templates' ? 'bg-navy text-white' : 'bg-gray-100 text-charcoal hover:bg-gray-200'}`}>
            Email Templates
          </button>
        </div>
      </div>

      {activeTab === 'applications' && (
        <>
          <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50"><tr><th className="px-6 py-3 text-left font-dm text-sm font-medium text-charcoal-muted">Name</th><th className="px-6 py-3 text-left font-dm text-sm font-medium text-charcoal-muted">Position</th><th className="px-6 py-3 text-left font-dm text-sm font-medium text-charcoal-muted">Experience</th><th className="px-6 py-3 text-left font-dm text-sm font-medium text-charcoal-muted">Status</th><th className="px-6 py-3 text-left font-dm text-sm font-medium text-charcoal-muted">Date</th><th className="px-6 py-3"></th></tr></thead>
              <tbody className="divide-y divide-gray-100">
                {applications.map((app) => (
                  <tr key={app.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4"><div><p className="font-dm text-sm text-charcoal font-medium">{app.name}</p><p className="font-dm text-xs text-gray-500">{app.email}</p></div></td>
                    <td className="px-6 py-4 font-dm text-sm text-charcoal-muted">{app.position}</td>
                    <td className="px-6 py-4 font-dm text-sm text-charcoal-muted capitalize">{app.experience}</td>
                    <td className="px-6 py-4">
                      <select value={app.status} onChange={(e) => updateStatus(app.id, e.target.value, app)} className="text-sm border rounded px-2 py-1 focus:outline-none focus:border-gold">
                        <option value="new">New</option><option value="reviewing">Under Review</option><option value="shortlisted">Shortlisted</option><option value="interview">Interview</option><option value="rejected">Rejected</option><option value="hired">Hired</option>
                      </select>
                      {app.interview_scheduled && <p className="text-xs text-purple-600 mt-1">📞 {app.interview_date} {app.interview_time}</p>}
                    </td>
                    <td className="px-6 py-4 font-dm text-sm text-charcoal-muted">{new Date(app.created_at).toLocaleDateString()}</td>
                    <td className="px-6 py-4"><button onClick={() => viewApplication(app.id)} className="text-blue-500 hover:text-blue-700"><Eye className="w-4 h-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {applications.length === 0 && <p className="text-center py-12 text-charcoal-muted">No applications yet</p>}
          </div>

          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="font-dm text-sm text-blue-800">
              <strong>📧 Automatic Email Notifications:</strong> Emails are automatically sent when you change application status. 
              "Application Received" email is sent immediately when someone applies.
            </p>
          </div>
        </>
      )}

      {activeTab === 'templates' && (
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <p className="font-dm text-sm text-amber-800">
              <strong>Available placeholders:</strong> {"{{applicant_name}}, {{position}}, {{application_id}}, {{applied_date}}, {{tracking_url}}, {{interview_date}}, {{interview_time}}"}
            </p>
          </div>

          {templates.map((template) => (
            <div key={template.template_key} className="bg-white rounded-lg border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <h3 className="font-dm font-medium text-charcoal">{template.name}</h3>
                  <p className="font-dm text-xs text-gray-500">{template.template_key}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setEditingTemplate({...template})} className="px-3 py-1 text-gold border border-gold rounded-lg font-dm text-sm hover:bg-gold/10">Edit</button>
                  {!template.is_default && (
                    <button onClick={() => resetTemplate(template.template_key)} className="px-3 py-1 text-gray-500 border border-gray-200 rounded-lg font-dm text-sm hover:bg-gray-50">Reset</button>
                  )}
                </div>
              </div>
              <div className="px-6 py-4">
                <p className="font-dm text-sm text-gray-600"><strong>Subject:</strong> {template.subject}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Application Detail Modal */}
      {selectedApp && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center"><h2 className="font-playfair text-xl text-charcoal">Application Details</h2><button onClick={() => setSelectedApp(null)}><X className="w-6 h-6" /></button></div>
            <div className="p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div><label className="font-dm text-xs text-charcoal-muted uppercase">Name</label><p className="font-dm text-charcoal">{selectedApp.name}</p></div>
                <div><label className="font-dm text-xs text-charcoal-muted uppercase">Email</label><p className="font-dm text-charcoal">{selectedApp.email}</p></div>
                <div><label className="font-dm text-xs text-charcoal-muted uppercase">Phone</label><p className="font-dm text-charcoal">{selectedApp.phone}</p></div>
                <div><label className="font-dm text-xs text-charcoal-muted uppercase">Position</label><p className="font-dm text-charcoal">{selectedApp.position}</p></div>
                <div><label className="font-dm text-xs text-charcoal-muted uppercase">Experience</label><p className="font-dm text-charcoal capitalize">{selectedApp.experience}</p></div>
                {selectedApp.linkedin && <div><label className="font-dm text-xs text-charcoal-muted uppercase">LinkedIn</label><a href={selectedApp.linkedin} target="_blank" rel="noreferrer" className="font-dm text-blue-500 hover:underline block truncate">{selectedApp.linkedin}</a></div>}
                {selectedApp.portfolio && <div><label className="font-dm text-xs text-charcoal-muted uppercase">Portfolio</label><a href={selectedApp.portfolio} target="_blank" rel="noreferrer" className="font-dm text-blue-500 hover:underline block truncate">{selectedApp.portfolio}</a></div>}
              </div>
              {selectedApp.cover_letter && <div><label className="font-dm text-xs text-charcoal-muted uppercase">Cover Letter</label><p className="font-dm text-charcoal-muted whitespace-pre-wrap">{selectedApp.cover_letter}</p></div>}
              {selectedApp.resume_filename && <div><label className="font-dm text-xs text-charcoal-muted uppercase">Resume</label><button onClick={() => downloadResume(selectedApp)} className="btn-outline text-sm flex items-center gap-2 mt-2"><FileText className="w-4 h-4" />{selectedApp.resume_filename}</button></div>}
            </div>
          </div>
        </div>
      )}

      {/* Template Edit Modal */}
      {editingTemplate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="bg-navy text-white px-6 py-4 flex items-center justify-between">
              <h2 className="font-playfair text-xl">Edit Template: {editingTemplate.name}</h2>
              <button onClick={() => setEditingTemplate(null)} className="text-white/70 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="space-y-4">
                <div>
                  <label className="block font-dm text-sm text-charcoal mb-1">Template Name</label>
                  <input type="text" value={editingTemplate.name} onChange={(e) => setEditingTemplate({...editingTemplate, name: e.target.value})} className="w-full px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold" />
                </div>
                <div>
                  <label className="block font-dm text-sm text-charcoal mb-1">Subject</label>
                  <input type="text" value={editingTemplate.subject} onChange={(e) => setEditingTemplate({...editingTemplate, subject: e.target.value})} className="w-full px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold" />
                </div>
                <div>
                  <label className="block font-dm text-sm text-charcoal mb-1">Body (HTML)</label>
                  <textarea value={editingTemplate.body} onChange={(e) => setEditingTemplate({...editingTemplate, body: e.target.value})} rows={15} className="w-full px-4 py-2 border border-gray-200 rounded-lg font-mono text-xs focus:outline-none focus:border-gold" />
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
              <button onClick={() => setEditingTemplate(null)} className="px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm hover:bg-gray-50">Cancel</button>
              <button onClick={saveTemplate} disabled={savingTemplate} className="px-4 py-2 bg-gold text-white rounded-lg font-dm text-sm hover:bg-gold/90 disabled:opacity-50">
                {savingTemplate ? 'Saving...' : 'Save Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Interview Scheduling Modal */}
      {showScheduleModal && schedulingApp && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="bg-purple-600 text-white px-6 py-4 rounded-t-xl flex items-center justify-between">
              <h2 className="font-playfair text-xl">Schedule Interview</h2>
              <button onClick={() => { setShowScheduleModal(false); setSchedulingApp(null); }} className="text-white/70 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6">
              <div className="bg-purple-50 rounded-lg p-4 mb-6">
                <p className="font-dm text-sm text-purple-800"><strong>Candidate:</strong> {schedulingApp.name}</p>
                <p className="font-dm text-sm text-purple-700">{schedulingApp.email} | {schedulingApp.phone}</p>
                <p className="font-dm text-sm text-purple-700 mt-1"><strong>Position:</strong> {schedulingApp.position}</p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block font-dm text-sm text-charcoal mb-1">Interview Date *</label>
                  <input type="date" value={interviewDate} onChange={(e) => setInterviewDate(e.target.value)} min={new Date().toISOString().split('T')[0]} className="w-full px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-purple-500" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block font-dm text-sm text-charcoal mb-1">Start Time (IST) *</label>
                    <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="w-full px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-purple-500" />
                  </div>
                  <div>
                    <label className="block font-dm text-sm text-charcoal mb-1">End Time (IST) *</label>
                    <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="w-full px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-purple-500" />
                  </div>
                </div>
                <div>
                  <label className="block font-dm text-sm text-charcoal mb-1">Notes (Optional)</label>
                  <textarea value={interviewNotes} onChange={(e) => setInterviewNotes(e.target.value)} rows={3} placeholder="Any special instructions..." className="w-full px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-purple-500" />
                </div>
              </div>
              
              <div className="mt-6 bg-blue-50 rounded-lg p-3">
                <p className="font-dm text-xs text-blue-700">📧 An email with interview details will be automatically sent to the candidate.</p>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
              <button onClick={() => { setShowScheduleModal(false); setSchedulingApp(null); }} className="px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm hover:bg-gray-50">Cancel</button>
              <button onClick={scheduleInterview} disabled={scheduling} className="px-4 py-2 bg-purple-600 text-white rounded-lg font-dm text-sm hover:bg-purple-700 disabled:opacity-50">
                {scheduling ? 'Scheduling...' : 'Schedule Interview'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Email Logs Component
const AdminEmailLogs = () => {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ total_sent: 0, total_failed: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterType !== 'all') params.append('email_type', filterType);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      
      const res = await axios.get(`${API}/admin/email-logs?${params.toString()}`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setLogs(res.data.logs || []);
      setStats(res.data.stats || { total_sent: 0, total_failed: 0 });
    } catch { toast.error('Failed to load email logs'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchLogs();
  }, [token, filterType, filterStatus]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchLogs();
  };

  const getStatusBadge = (status) => {
    const styles = {
      sent: 'bg-green-100 text-green-700',
      failed: 'bg-red-100 text-red-700',
      pending: 'bg-yellow-100 text-yellow-700'
    };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>{status}</span>;
  };

  const getEmailTypeBadge = (type) => {
    const typeLabels = {
      'application_received': 'App Received',
      'application_reviewing': 'Under Review',
      'application_shortlisted': 'Shortlisted',
      'application_interview': 'Interview',
      'application_rejected': 'Rejected',
      'application_hired': 'Hired',
      'candidate_invitation': 'Portal Invite',
      'offer_sent': 'Offer Sent',
      'otp_verification': 'OTP',
      'offer_accepted': 'Offer Accepted',
      'document_uploaded': 'Doc Upload',
      'document_approved': 'Doc Approved',
      'document_rejected': 'Doc Rejected'
    };
    return typeLabels[type] || type;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const istOffset = 5.5 * 60 * 60 * 1000;
    const istDate = new Date(date.getTime() + istOffset);
    return istDate.toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'UTC'
    }) + ' IST';
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-playfair text-2xl text-charcoal">Email Logs</h1>
        <div className="flex gap-4">
          <div className="flex items-center gap-2 bg-green-50 px-4 py-2 rounded-lg">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span className="font-dm text-sm text-green-700">{stats.total_sent} Sent</span>
          </div>
          <div className="flex items-center gap-2 bg-red-50 px-4 py-2 rounded-lg">
            <XCircle className="w-4 h-4 text-red-600" />
            <span className="font-dm text-sm text-red-700">{stats.total_failed} Failed</span>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg border border-gray-100 p-4 mb-6">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block font-dm text-xs text-charcoal-muted mb-1">Search by Email or Name</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search email, name, or subject..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold"
              />
            </div>
          </div>
          <div>
            <label className="block font-dm text-xs text-charcoal-muted mb-1">Email Type</label>
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold">
              <option value="all">All Types</option>
              <optgroup label="Applications">
                <option value="application_received">App Received</option>
                <option value="application_reviewing">Under Review</option>
                <option value="application_shortlisted">Shortlisted</option>
                <option value="application_interview">Interview</option>
                <option value="application_rejected">Rejected</option>
                <option value="application_hired">Hired</option>
              </optgroup>
              <optgroup label="Pre-Joining">
                <option value="candidate_invitation">Portal Invite</option>
                <option value="offer_sent">Offer Sent</option>
                <option value="otp_verification">OTP</option>
                <option value="offer_accepted">Offer Accepted</option>
                <option value="document_uploaded">Doc Upload</option>
                <option value="document_approved">Doc Approved</option>
                <option value="document_rejected">Doc Rejected</option>
              </optgroup>
            </select>
          </div>
          <div>
            <label className="block font-dm text-xs text-charcoal-muted mb-1">Status</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold">
              <option value="all">All Status</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          <button type="submit" className="px-4 py-2 bg-navy text-white rounded-lg font-dm text-sm hover:bg-navy/90">
            Search
          </button>
        </form>
      </div>

      {/* Logs Table */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-gold/30 border-t-gold rounded-full animate-spin"></div></div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left font-dm text-sm font-medium text-charcoal-muted">Recipient</th>
                <th className="px-6 py-3 text-left font-dm text-sm font-medium text-charcoal-muted">Subject</th>
                <th className="px-6 py-3 text-left font-dm text-sm font-medium text-charcoal-muted">Type</th>
                <th className="px-6 py-3 text-left font-dm text-sm font-medium text-charcoal-muted">Status</th>
                <th className="px-6 py-3 text-left font-dm text-sm font-medium text-charcoal-muted">Sent At (IST)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-dm text-sm text-charcoal font-medium">{log.recipient_name}</p>
                      <p className="font-dm text-xs text-gray-500">{log.to_email}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="font-dm text-sm text-charcoal-muted truncate max-w-[300px]">{log.subject}</p>
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium">{getEmailTypeBadge(log.email_type)}</span>
                  </td>
                  <td className="px-6 py-4">{getStatusBadge(log.status)}</td>
                  <td className="px-6 py-4 font-dm text-sm text-charcoal-muted">{formatDate(log.sent_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {logs.length === 0 && <p className="text-center py-12 text-charcoal-muted">No email logs found</p>}
        </div>
      )}
    </div>
  );
};

// Interviews Management Component
const AdminInterviews = () => {
  const { token } = useAuth();
  const [interviews, setInterviews] = useState([]);
  const [stats, setStats] = useState({ total: 0, today: 0, upcoming: 0, completed: 0 });
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Reschedule modal state
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleInterview, setRescheduleInterview] = useState(null);
  const [newDate, setNewDate] = useState('');
  const [newStartTime, setNewStartTime] = useState('');
  const [newEndTime, setNewEndTime] = useState('');
  const [rescheduleNotes, setRescheduleNotes] = useState('');
  const [rescheduling, setRescheduling] = useState(false);

  const fetchInterviews = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      
      const res = await axios.get(`${API}/admin/interviews?${params.toString()}`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setInterviews(res.data.interviews || []);
      setStats(res.data.stats || { total: 0, today: 0, upcoming: 0, completed: 0 });
    } catch { toast.error('Failed to load interviews'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchInterviews(); }, [token, filterStatus]);

  const updateInterviewStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/interviews/${id}/status?status=${status}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setInterviews(interviews.map(i => i.id === id ? {...i, status} : i));
      toast.success('Interview status updated');
    } catch { toast.error('Failed to update status'); }
  };

  const openRescheduleModal = (interview) => {
    setRescheduleInterview(interview);
    setNewDate(interview.interview_date);
    setNewStartTime(interview.start_time);
    setNewEndTime(interview.end_time);
    setRescheduleNotes('');
    setShowRescheduleModal(true);
  };

  const handleReschedule = async () => {
    if (!rescheduleInterview || !newDate || !newStartTime || !newEndTime) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setRescheduling(true);
    try {
      await axios.put(`${API}/admin/interviews/${rescheduleInterview.id}/reschedule`, {
        interview_date: newDate,
        start_time: newStartTime,
        end_time: newEndTime,
        notes: rescheduleNotes
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      setInterviews(interviews.map(i => i.id === rescheduleInterview.id ? {
        ...i,
        interview_date: newDate,
        start_time: newStartTime,
        end_time: newEndTime,
        status: 'scheduled'
      } : i));
      
      setShowRescheduleModal(false);
      setRescheduleInterview(null);
      toast.success('Interview rescheduled & email sent to candidate!');
    } catch { toast.error('Failed to reschedule interview'); }
    finally { setRescheduling(false); }
  };

  const deleteInterview = async (id) => {
    if (!window.confirm('Cancel this interview? The application will be moved back to Shortlisted.')) return;
    try {
      await axios.delete(`${API}/admin/interviews/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setInterviews(interviews.filter(i => i.id !== id));
      toast.success('Interview cancelled');
    } catch { toast.error('Failed to cancel interview'); }
  };

  const getStatusBadge = (status) => {
    const styles = {
      scheduled: 'bg-purple-100 text-purple-700',
      completed: 'bg-green-100 text-green-700',
      cancelled: 'bg-gray-100 text-gray-700',
      no_show: 'bg-red-100 text-red-700'
    };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.scheduled}`}>{status.replace('_', ' ')}</span>;
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return dateStr; }
  };

  const isToday = (dateStr) => {
    const today = new Date().toISOString().split('T')[0];
    return dateStr === today;
  };

  const isPast = (dateStr) => {
    const today = new Date().toISOString().split('T')[0];
    return dateStr < today;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-playfair text-2xl text-charcoal">Interview Schedule</h1>
        <div className="flex gap-4">
          <div className="flex items-center gap-2 bg-purple-50 px-4 py-2 rounded-lg">
            <span className="font-dm text-sm text-purple-700"><strong>{stats.today}</strong> Today</span>
          </div>
          <div className="flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-lg">
            <span className="font-dm text-sm text-blue-700"><strong>{stats.upcoming}</strong> Upcoming</span>
          </div>
          <div className="flex items-center gap-2 bg-green-50 px-4 py-2 rounded-lg">
            <span className="font-dm text-sm text-green-700"><strong>{stats.completed}</strong> Completed</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-100 p-4 mb-6">
        <div className="flex gap-4 items-center">
          <div>
            <label className="block font-dm text-xs text-charcoal-muted mb-1">Filter by Status</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-purple-500">
              <option value="all">All Interviews</option>
              <option value="scheduled">Scheduled</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="no_show">No Show</option>
            </select>
          </div>
          <button onClick={fetchInterviews} className="mt-5 px-4 py-2 bg-purple-600 text-white rounded-lg font-dm text-sm hover:bg-purple-700">
            Refresh
          </button>
        </div>
      </div>

      {/* Interviews Table */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-purple-300 border-t-purple-600 rounded-full animate-spin"></div></div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left font-dm text-sm font-medium text-charcoal-muted">Candidate</th>
                <th className="px-6 py-3 text-left font-dm text-sm font-medium text-charcoal-muted">Position</th>
                <th className="px-6 py-3 text-left font-dm text-sm font-medium text-charcoal-muted">Date</th>
                <th className="px-6 py-3 text-left font-dm text-sm font-medium text-charcoal-muted">Time (IST)</th>
                <th className="px-6 py-3 text-left font-dm text-sm font-medium text-charcoal-muted">Status</th>
                <th className="px-6 py-3 text-left font-dm text-sm font-medium text-charcoal-muted">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {interviews.map((interview) => (
                <tr key={interview.id} className={`hover:bg-gray-50 ${isToday(interview.interview_date) ? 'bg-purple-50' : isPast(interview.interview_date) && interview.status === 'scheduled' ? 'bg-red-50' : ''}`}>
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-dm text-sm text-charcoal font-medium">{interview.candidate_name}</p>
                      <p className="font-dm text-xs text-gray-500">{interview.candidate_email}</p>
                      <p className="font-dm text-xs text-gray-500">📞 {interview.candidate_phone}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 font-dm text-sm text-charcoal-muted">{interview.position}</td>
                  <td className="px-6 py-4">
                    <p className="font-dm text-sm text-charcoal font-medium">{formatDate(interview.interview_date)}</p>
                    {isToday(interview.interview_date) && <span className="text-xs bg-purple-600 text-white px-2 py-0.5 rounded">TODAY</span>}
                  </td>
                  <td className="px-6 py-4 font-dm text-sm font-medium text-charcoal">{interview.start_time} - {interview.end_time}</td>
                  <td className="px-6 py-4">
                    <select value={interview.status} onChange={(e) => updateInterviewStatus(interview.id, e.target.value)} className="text-sm border rounded px-2 py-1 focus:outline-none focus:border-purple-500">
                      <option value="scheduled">Scheduled</option>
                      <option value="completed">Completed</option>
                      <option value="no_show">No Show</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <button onClick={() => openRescheduleModal(interview)} className="text-blue-500 hover:text-blue-700 text-sm" title="Reschedule">Reschedule</button>
                      <button onClick={() => deleteInterview(interview.id)} className="text-red-500 hover:text-red-700 text-sm" title="Cancel">Cancel</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {interviews.length === 0 && <p className="text-center py-12 text-charcoal-muted">No interviews scheduled</p>}
        </div>
      )}

      {/* Legend */}
      <div className="mt-4 flex gap-6 text-sm font-dm text-charcoal-muted">
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-purple-50 border border-purple-200"></span> Today's Interview</span>
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-red-50 border border-red-200"></span> Overdue (Past date, still scheduled)</span>
      </div>

      {/* Reschedule Modal */}
      {showRescheduleModal && rescheduleInterview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="bg-orange-500 text-white px-6 py-4 rounded-t-xl flex items-center justify-between">
              <h2 className="font-playfair text-xl">Reschedule Interview</h2>
              <button onClick={() => { setShowRescheduleModal(false); setRescheduleInterview(null); }} className="text-white/70 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6">
              <div className="bg-orange-50 rounded-lg p-4 mb-6">
                <p className="font-dm text-sm text-orange-800"><strong>Candidate:</strong> {rescheduleInterview.candidate_name}</p>
                <p className="font-dm text-sm text-orange-700">{rescheduleInterview.candidate_email}</p>
                <p className="font-dm text-sm text-orange-700 mt-1"><strong>Current:</strong> {formatDate(rescheduleInterview.interview_date)} at {rescheduleInterview.start_time} - {rescheduleInterview.end_time}</p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block font-dm text-sm text-charcoal mb-1">New Date *</label>
                  <input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} min={new Date().toISOString().split('T')[0]} className="w-full px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-orange-500" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block font-dm text-sm text-charcoal mb-1">Start Time (IST) *</label>
                    <input type="time" value={newStartTime} onChange={(e) => setNewStartTime(e.target.value)} className="w-full px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-orange-500" />
                  </div>
                  <div>
                    <label className="block font-dm text-sm text-charcoal mb-1">End Time (IST) *</label>
                    <input type="time" value={newEndTime} onChange={(e) => setNewEndTime(e.target.value)} className="w-full px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-orange-500" />
                  </div>
                </div>
                <div>
                  <label className="block font-dm text-sm text-charcoal mb-1">Reason/Notes (Optional)</label>
                  <textarea value={rescheduleNotes} onChange={(e) => setRescheduleNotes(e.target.value)} rows={2} placeholder="Reason for rescheduling..." className="w-full px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-orange-500" />
                </div>
              </div>
              
              <div className="mt-6 bg-blue-50 rounded-lg p-3">
                <p className="font-dm text-xs text-blue-700">📧 A reschedule notification email will be automatically sent to the candidate with the new interview details.</p>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
              <button onClick={() => { setShowRescheduleModal(false); setRescheduleInterview(null); }} className="px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm hover:bg-gray-50">Cancel</button>
              <button onClick={handleReschedule} disabled={rescheduling} className="px-4 py-2 bg-orange-500 text-white rounded-lg font-dm text-sm hover:bg-orange-600 disabled:opacity-50">
                {rescheduling ? 'Rescheduling...' : 'Reschedule & Notify'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Offer Letter Generator Component
const AdminOfferLetterGenerator = () => {
  const { token } = useAuth();
  const [candidateName, setCandidateName] = useState('');
  const [position, setPosition] = useState('Process Associate');
  const [joiningDate, setJoiningDate] = useState('');
  const [salary, setSalary] = useState('');
  const [salaryWords, setSalaryWords] = useState('');
  const [generating, setGenerating] = useState(false);

  // Convert number to words
  const numberToWords = (num) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
      'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    
    if (num === 0) return 'Zero';
    if (num < 20) return ones[num];
    if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 ? '-' + ones[num % 10] : '');
    if (num < 1000) return ones[Math.floor(num / 100)] + ' Hundred' + (num % 100 ? ' ' + numberToWords(num % 100) : '');
    if (num < 100000) return numberToWords(Math.floor(num / 1000)) + ' Thousand' + (num % 1000 ? ' ' + numberToWords(num % 1000) : '');
    if (num < 10000000) return numberToWords(Math.floor(num / 100000)) + ' Lakh' + (num % 100000 ? ' ' + numberToWords(num % 100000) : '');
    return numberToWords(Math.floor(num / 10000000)) + ' Crore' + (num % 10000000 ? ' ' + numberToWords(num % 10000000) : '');
  };

  const handleSalaryChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setSalary(value);
    if (value) {
      setSalaryWords(numberToWords(parseInt(value)));
    } else {
      setSalaryWords('');
    }
  };

  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' });
  };

  const generateOfferLetter = async () => {
    if (!candidateName || !joiningDate || !salary) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/admin/generate-offer-letter`, {
        candidate_name: candidateName,
        position: position,
        joining_date: formatDateForDisplay(joiningDate),
        salary: parseInt(salary),
        salary_words: salaryWords
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      // Download the PDF
      const linkSource = `data:${response.data.file_type};base64,${response.data.file_data}`;
      const downloadLink = document.createElement('a');
      downloadLink.href = linkSource;
      downloadLink.download = response.data.file_name;
      downloadLink.click();
      
      toast.success('Offer letter generated and downloaded!');
    } catch (err) {
      toast.error('Failed to generate offer letter');
    }
    finally { setGenerating(false); }
  };

  return (
    <div>
      <h1 className="font-playfair text-2xl text-charcoal mb-6">Offer Letter Generator</h1>
      
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Form Section */}
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h2 className="font-playfair text-lg text-charcoal mb-6">Enter Candidate Details</h2>
          
          <div className="space-y-5">
            <div>
              <label className="block font-dm text-sm text-charcoal mb-2">Candidate Name *</label>
              <input
                type="text"
                value={candidateName}
                onChange={(e) => setCandidateName(e.target.value)}
                placeholder="e.g., Nusrat Banu"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold"
              />
            </div>
            
            <div>
              <label className="block font-dm text-sm text-charcoal mb-2">Position *</label>
              <select
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold"
              >
                <option value="Process Associate">Process Associate</option>
                <option value="Senior Process Associate">Senior Process Associate</option>
                <option value="Team Lead">Team Lead</option>
                <option value="Business Analyst">Business Analyst</option>
                <option value="Software Developer">Software Developer</option>
                <option value="Digital Marketing Executive">Digital Marketing Executive</option>
                <option value="SEO Specialist">SEO Specialist</option>
                <option value="Content Writer">Content Writer</option>
                <option value="Graphic Designer">Graphic Designer</option>
                <option value="HR Executive">HR Executive</option>
              </select>
            </div>
            
            <div>
              <label className="block font-dm text-sm text-charcoal mb-2">Joining Date *</label>
              <input
                type="date"
                value={joiningDate}
                onChange={(e) => setJoiningDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold"
              />
              {joiningDate && (
                <p className="text-xs text-gray-500 mt-1">Will appear as: {formatDateForDisplay(joiningDate)}</p>
              )}
            </div>
            
            <div>
              <label className="block font-dm text-sm text-charcoal mb-2">Annual Salary (INR) *</label>
              <input
                type="text"
                value={salary ? parseInt(salary).toLocaleString('en-IN') : ''}
                onChange={handleSalaryChange}
                placeholder="e.g., 276000"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold"
              />
              {salaryWords && (
                <p className="text-xs text-green-600 mt-1">In words: Rupees {salaryWords} only</p>
              )}
            </div>
            
            <button
              onClick={generateOfferLetter}
              disabled={generating || !candidateName || !joiningDate || !salary}
              className="w-full py-3 bg-navy text-white rounded-lg font-dm text-sm hover:bg-navy/90 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {generating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Generating...
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4" />
                  Generate & Download Offer Letter
                </>
              )}
            </button>
          </div>
        </div>
        
        {/* Preview Section */}
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h2 className="font-playfair text-lg text-charcoal mb-6">Preview</h2>
          
          <div className="bg-gray-50 rounded-lg p-6 border-2 border-dashed border-gray-200 min-h-[400px]">
            {candidateName ? (
              <div className="space-y-4 text-sm">
                <div className="border-b border-gray-200 pb-4">
                  <p className="font-bold text-navy text-lg">CREATIVE</p>
                  <p className="text-navy">Infotech</p>
                  <p className="text-gold font-semibold mt-2">Offer of Employment</p>
                </div>
                
                <div>
                  <p className="text-gray-500">Date: {new Date().toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' })}</p>
                  <p className="mt-2">To,</p>
                  <p className="font-semibold">Ms./Mr. {candidateName}</p>
                </div>
                
                <div>
                  <p className="font-semibold">Subject: Offer of Employment for the Position of {position}</p>
                </div>
                
                <div>
                  <p>Dear {candidateName},</p>
                  <p className="text-gray-600 mt-2">It gives us great pleasure to welcome you to the Creative Infotech family!</p>
                </div>
                
                <div className="bg-white p-4 rounded border">
                  <p className="font-semibold text-navy">Key Details:</p>
                  <ul className="mt-2 space-y-1 text-gray-600">
                    <li>• Position: <strong>{position}</strong></li>
                    <li>• Joining Date: <strong>{joiningDate ? formatDateForDisplay(joiningDate) : '---'}</strong></li>
                    <li>• Annual Salary: <strong>INR {salary ? parseInt(salary).toLocaleString('en-IN') : '---'}/-</strong></li>
                    {salaryWords && <li className="text-xs text-gray-500">({salaryWords} only)</li>}
                  </ul>
                </div>
                
                <p className="text-xs text-gray-400 mt-4">This is a preview. The actual PDF will include all terms, conditions, and documentation requirements.</p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <FileText className="w-16 h-16 mb-4 opacity-50" />
                <p>Enter candidate details to see preview</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Info Box */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="font-dm text-sm text-blue-800">
          <strong>📄 Offer Letter Template:</strong> The generated PDF follows the official Creative Infotech offer letter format with:
        </p>
        <ul className="mt-2 text-sm text-blue-700 list-disc list-inside">
          <li>Company header with colorful branding</li>
          <li>Position & Joining Date details</li>
          <li>Compensation section with salary in figures and words</li>
          <li>Documentation requirements checklist</li>
          <li>Terms & Conditions of employment</li>
          <li>Acceptance section with candidate signature area</li>
        </ul>
      </div>
    </div>
  );
};

const AdminCMS = () => {
  const { token } = useAuth();
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState('home');
  const [content, setContent] = useState({});
  const [saving, setSaving] = useState(false);

  const pagesList = [
    { id: 'home', name: 'Home Page', fields: ['heroTitle', 'heroSubtitle', 'heroDescription'] },
    { id: 'about', name: 'About Page', fields: ['title', 'description', 'story'] },
    { id: 'services', name: 'Services Page', fields: ['title', 'description'] },
    { id: 'contact', name: 'Contact Page', fields: ['title', 'description', 'email', 'address'] },
    { id: 'careers', name: 'Careers Page', fields: ['title', 'description'] },
  ];

  useEffect(() => {
    axios.get(`${API}/content/${selectedPage}`).then(res => setContent(res.data?.content || {})).catch(() => setContent({}));
  }, [selectedPage]);

  const saveContent = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/content/${selectedPage}`, { content }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Content saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <h1 className="font-playfair text-2xl text-charcoal mb-6">Content Management</h1>
      <div className="grid lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg border border-gray-100 p-4">
          <h3 className="font-dm font-medium text-charcoal mb-4">Pages</h3>
          {pagesList.map((page) => (
            <button key={page.id} onClick={() => setSelectedPage(page.id)} className={`w-full text-left px-3 py-2 rounded-lg mb-1 font-dm text-sm ${selectedPage === page.id ? 'bg-gold/10 text-gold' : 'text-charcoal-muted hover:bg-gray-50'}`}>{page.name}</button>
          ))}
        </div>
        <div className="lg:col-span-3 bg-white rounded-lg border border-gray-100 p-6">
          <h3 className="font-playfair text-xl text-charcoal mb-6">{pagesList.find(p => p.id === selectedPage)?.name}</h3>
          <div className="space-y-4">
            {pagesList.find(p => p.id === selectedPage)?.fields.map((field) => (
              <div key={field}>
                <label className="font-dm text-sm text-charcoal font-medium mb-2 block capitalize">{field.replace(/([A-Z])/g, ' $1').trim()}</label>
                {field.includes('description') || field.includes('story') ? (
                  <textarea value={content[field] || ''} onChange={(e) => setContent({...content, [field]: e.target.value})} rows={4} className="input-elegant resize-none" />
                ) : (
                  <input type="text" value={content[field] || ''} onChange={(e) => setContent({...content, [field]: e.target.value})} className="input-elegant" />
                )}
              </div>
            ))}
            <button onClick={saveContent} disabled={saving} className="btn-primary mt-6">{saving ? 'Saving...' : 'Save Changes'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ ADMIN ANALYTICS ============

const AdminAnalytics = () => {
  const { token } = useAuth();
  const [overview, setOverview] = useState(null);
  const [geo, setGeo] = useState(null);
  const [devices, setDevices] = useState(null);
  const [pages, setPages] = useState(null);
  const [referrers, setReferrers] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [recentVisitors, setRecentVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API}/admin/analytics/overview`, { headers }),
      axios.get(`${API}/admin/analytics/geo`, { headers }),
      axios.get(`${API}/admin/analytics/devices`, { headers }),
      axios.get(`${API}/admin/analytics/pages`, { headers }),
      axios.get(`${API}/admin/analytics/referrers`, { headers }),
      axios.get(`${API}/admin/analytics/timeline?days=30`, { headers }),
      axios.get(`${API}/admin/analytics/recent?limit=20`, { headers })
    ])
    .then(([overviewRes, geoRes, devicesRes, pagesRes, referrersRes, timelineRes, recentRes]) => {
      setOverview(overviewRes.data);
      setGeo(geoRes.data);
      setDevices(devicesRes.data);
      setPages(pagesRes.data);
      setReferrers(referrersRes.data);
      setTimeline(timelineRes.data);
      setRecentVisitors(recentRes.data.visitors);
      setLoading(false);
    })
    .catch(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-gold/30 border-t-gold rounded-full animate-spin"></div></div>;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Activity },
    { id: 'geo', label: 'Geography', icon: Globe },
    { id: 'devices', label: 'Devices', icon: Monitor },
    { id: 'pages', label: 'Pages', icon: FileText },
    { id: 'visitors', label: 'Recent Visitors', icon: Users }
  ];

  const getDeviceIcon = (type) => {
    switch(type?.toLowerCase()) {
      case 'mobile': return <Smartphone className="w-4 h-4" />;
      case 'tablet': return <Tablet className="w-4 h-4" />;
      default: return <Monitor className="w-4 h-4" />;
    }
  };

  return (
    <div>
      <h1 className="font-playfair text-2xl text-charcoal mb-6">Website Analytics</h1>
      
      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <div className="bg-white rounded-lg p-4 border border-gray-100">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center"><MousePointer className="w-5 h-5 text-white" /></div>
          </div>
          <p className="font-playfair text-2xl text-charcoal">{overview?.total_visits || 0}</p>
          <p className="font-dm text-xs text-charcoal-muted">Total Visits</p>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-100">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center"><TrendingUp className="w-5 h-5 text-white" /></div>
          </div>
          <p className="font-playfair text-2xl text-charcoal">{overview?.today_visits || 0}</p>
          <p className="font-dm text-xs text-charcoal-muted">Today</p>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-100">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-purple-500 flex items-center justify-center"><Activity className="w-5 h-5 text-white" /></div>
          </div>
          <p className="font-playfair text-2xl text-charcoal">{overview?.week_visits || 0}</p>
          <p className="font-dm text-xs text-charcoal-muted">This Week</p>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-100">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-orange-500 flex items-center justify-center"><BarChart3 className="w-5 h-5 text-white" /></div>
          </div>
          <p className="font-playfair text-2xl text-charcoal">{overview?.month_visits || 0}</p>
          <p className="font-dm text-xs text-charcoal-muted">This Month</p>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-100">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-gold flex items-center justify-center"><Users className="w-5 h-5 text-white" /></div>
          </div>
          <p className="font-playfair text-2xl text-charcoal">{overview?.unique_visitors || 0}</p>
          <p className="font-dm text-xs text-charcoal-muted">Unique Visitors</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-dm text-sm whitespace-nowrap transition-colors ${activeTab === tab.id ? 'bg-navy text-white' : 'bg-white text-charcoal-muted hover:bg-gray-50 border border-gray-100'}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Timeline Chart */}
          <div className="bg-white rounded-lg border border-gray-100 p-6">
            <h3 className="font-playfair text-lg text-charcoal mb-4">Visits Over Time (30 Days)</h3>
            <div className="h-48 flex items-end gap-1">
              {timeline?.timeline?.map((day, i) => {
                const maxVisits = Math.max(...(timeline?.timeline?.map(d => d.visits) || [1]));
                const height = (day.visits / maxVisits) * 100;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full bg-gold/20 rounded-t relative group" style={{ height: `${Math.max(height, 5)}%` }}>
                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-charcoal text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        {day.date}: {day.visits}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top Pages */}
          <div className="bg-white rounded-lg border border-gray-100 p-6">
            <h3 className="font-playfair text-lg text-charcoal mb-4">Top Pages</h3>
            <div className="space-y-3">
              {pages?.pages?.slice(0, 8).map((page, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="font-dm text-sm text-charcoal truncate flex-1">{page.page}</span>
                  <span className="font-dm text-sm text-charcoal-muted ml-2">{page.visits} visits</span>
                </div>
              ))}
            </div>
          </div>

          {/* Referrers */}
          <div className="bg-white rounded-lg border border-gray-100 p-6">
            <h3 className="font-playfair text-lg text-charcoal mb-4">Traffic Sources</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="font-dm text-sm text-charcoal">Direct Traffic</span>
                <span className="font-dm text-sm text-charcoal-muted">{referrers?.direct_visits || 0} visits</span>
              </div>
              {referrers?.referrers?.slice(0, 5).map((ref, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="font-dm text-sm text-charcoal truncate flex-1">{ref.source}</span>
                  <span className="font-dm text-sm text-charcoal-muted ml-2">{ref.visits} visits</span>
                </div>
              ))}
            </div>
          </div>

          {/* Device Types */}
          <div className="bg-white rounded-lg border border-gray-100 p-6">
            <h3 className="font-playfair text-lg text-charcoal mb-4">Device Types</h3>
            <div className="space-y-3">
              {devices?.devices?.map((device, i) => (
                <div key={i} className="flex items-center gap-3">
                  {getDeviceIcon(device.name)}
                  <span className="font-dm text-sm text-charcoal flex-1">{device.name}</span>
                  <span className="font-dm text-sm text-charcoal-muted">{device.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'geo' && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Countries */}
          <div className="bg-white rounded-lg border border-gray-100 p-6">
            <h3 className="font-playfair text-lg text-charcoal mb-4">Visitors by Country</h3>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {geo?.countries?.map((country, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xl">{country.country_code === 'IN' ? '🇮🇳' : country.country_code === 'US' ? '🇺🇸' : country.country_code === 'GB' ? '🇬🇧' : '🌍'}</span>
                  <span className="font-dm text-sm text-charcoal flex-1">{country.country}</span>
                  <span className="font-dm text-sm text-charcoal-muted">{country.visits} visits</span>
                </div>
              ))}
              {(!geo?.countries || geo.countries.length === 0) && <p className="text-charcoal-muted text-sm">No data yet</p>}
            </div>
          </div>

          {/* Cities */}
          <div className="bg-white rounded-lg border border-gray-100 p-6">
            <h3 className="font-playfair text-lg text-charcoal mb-4">Top Cities</h3>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {geo?.cities?.map((city, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div>
                    <span className="font-dm text-sm text-charcoal">{city.city}</span>
                    <span className="font-dm text-xs text-charcoal-muted ml-2">({city.country})</span>
                  </div>
                  <span className="font-dm text-sm text-charcoal-muted">{city.visits} visits</span>
                </div>
              ))}
              {(!geo?.cities || geo.cities.length === 0) && <p className="text-charcoal-muted text-sm">No data yet</p>}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'devices' && (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Browsers */}
          <div className="bg-white rounded-lg border border-gray-100 p-6">
            <h3 className="font-playfair text-lg text-charcoal mb-4">Browsers</h3>
            <div className="space-y-3">
              {devices?.browsers?.map((browser, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="font-dm text-sm text-charcoal">{browser.name}</span>
                  <span className="font-dm text-sm text-charcoal-muted">{browser.count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Operating Systems */}
          <div className="bg-white rounded-lg border border-gray-100 p-6">
            <h3 className="font-playfair text-lg text-charcoal mb-4">Operating Systems</h3>
            <div className="space-y-3">
              {devices?.operating_systems?.map((os, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="font-dm text-sm text-charcoal">{os.name}</span>
                  <span className="font-dm text-sm text-charcoal-muted">{os.count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Screen Resolutions */}
          <div className="bg-white rounded-lg border border-gray-100 p-6">
            <h3 className="font-playfair text-lg text-charcoal mb-4">Screen Resolutions</h3>
            <div className="space-y-3">
              {devices?.screen_resolutions?.map((screen, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="font-dm text-sm text-charcoal">{screen.resolution}</span>
                  <span className="font-dm text-sm text-charcoal-muted">{screen.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'pages' && (
        <div className="bg-white rounded-lg border border-gray-100 p-6">
          <h3 className="font-playfair text-lg text-charcoal mb-4">All Pages</h3>
          <table className="w-full">
            <thead><tr><th className="text-left font-dm text-sm text-charcoal-muted pb-3">Page</th><th className="text-right font-dm text-sm text-charcoal-muted pb-3">Visits</th></tr></thead>
            <tbody>
              {pages?.pages?.map((page, i) => (
                <tr key={i} className="border-t border-gray-50">
                  <td className="py-3 font-dm text-sm text-charcoal">{page.page}</td>
                  <td className="py-3 font-dm text-sm text-charcoal-muted text-right">{page.visits}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'visitors' && (
        <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-dm text-xs text-charcoal-muted">Time</th>
                  <th className="px-4 py-3 text-left font-dm text-xs text-charcoal-muted">Page</th>
                  <th className="px-4 py-3 text-left font-dm text-xs text-charcoal-muted">Location</th>
                  <th className="px-4 py-3 text-left font-dm text-xs text-charcoal-muted">Device</th>
                  <th className="px-4 py-3 text-left font-dm text-xs text-charcoal-muted">Browser</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {recentVisitors.map((visitor, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-dm text-xs text-charcoal-muted">{new Date(visitor.timestamp).toLocaleString()}</td>
                    <td className="px-4 py-3 font-dm text-sm text-charcoal">{visitor.page}</td>
                    <td className="px-4 py-3 font-dm text-sm text-charcoal">{visitor.city}, {visitor.country}</td>
                    <td className="px-4 py-3 font-dm text-sm text-charcoal-muted">{visitor.device_type}</td>
                    <td className="px-4 py-3 font-dm text-sm text-charcoal-muted">{visitor.browser} / {visitor.os}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ============ VISITOR TRACKING WRAPPER ============

const VisitorTrackingWrapper = ({ children }) => {
  useVisitorTracking();
  return children;
};

// ============ APP ============

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors toastOptions={{ style: { fontFamily: 'DM Sans, sans-serif' } }} />
      <BrowserRouter>
        <VisitorTrackingWrapper>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<ProtectedRoute><AdminLayout><AdminDashboard /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/analytics" element={<ProtectedRoute><AdminLayout><AdminAnalytics /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/contacts" element={<ProtectedRoute><AdminLayout><AdminContacts /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/newsletter" element={<ProtectedRoute><AdminLayout><AdminNewsletter /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/jobs" element={<ProtectedRoute><AdminLayout><AdminJobs /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/applications" element={<ProtectedRoute><AdminLayout><AdminApplications /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/email-logs" element={<ProtectedRoute><AdminLayout><AdminEmailLogs /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/interviews" element={<ProtectedRoute><AdminLayout><AdminInterviews /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/offer-letter" element={<ProtectedRoute><AdminLayout><AdminOfferLetterGenerator /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/prejoining" element={<ProtectedRoute><AdminLayout><PreJoiningAdmin /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/training" element={<ProtectedRoute><AdminLayout><JoinedPortalAdmin /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/cms" element={<ProtectedRoute><AdminLayout><AdminCMS /></AdminLayout></ProtectedRoute>} />
            
            {/* Pre-Joining Portal Routes (Candidate Access) */}
            <Route path="/prejoining" element={<PreJoiningLogin />} />
            <Route path="/prejoining/dashboard" element={<PreJoiningDashboard />} />
            
            {/* Joined Portal Routes (Training - New Joinee Access) */}
            <Route path="/joined" element={<JoinedPortalLogin />} />
            <Route path="/joined/dashboard" element={<JoinedPortalDashboard />} />
          </Routes>
        </VisitorTrackingWrapper>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
